import { EditorState } from 'draft-js';
import emojiStrip from 'emoji-strip';
import moment from 'moment';
import { OnlyFansSextforceBigBrother, OnlyFansSubscriber } from '../hooks/useSubscriber';
import { hasCommonStrings } from './common';

/**
 * Parses the raw follow-back data and filters it out by user group
 * @param targetUsers Target users group
 * @param data Raw data array from the server
 * @returns Parased array of data filtered by given group
 */
export const parseMessageUsersSessionData = (targetUsers: string, data: any[]): any[] => {
    let mainFiltered: any[] = [];

    if (targetUsers === 'all') {
        mainFiltered = data;
    } else {
        mainFiltered = data.filter(item => {
            if (targetUsers === 'fansExpiredPriority') {
                return item.targetUsers === 'fansExpired' && item.filter;
            } else if (targetUsers === 'fansExpired') {
                return item.targetUsers === 'fansExpired' && !item.filter;
            } else {
                return item.targetUsers === targetUsers;
            }
        });
    }

    const result = mainFiltered.map(item => {
        return {
            time: moment(item.startedAt).valueOf(),
            targetUsers: item.targetUsers,
            priority: item.filter ? true : false,
            messagesSent: item.messagesSent || 0,
        };
    });

    return result;
};

/**
 * Sums up the total number of users followed in the given parsed data array
 * @param data Parsed array of follow back data
 * @returns Total followed number
 */
export const messagesSentParsedDataTotalSent = (data: any[]): number => {
    let total = 0;

    data.forEach(item => (total += item.messagesSent ? item.messagesSent : 0));

    return total;
};

/**
 * Identifies the type of a given wallet address
 * @param word Wallet address to identify
 * @returns Type of the wallet address
 */
export const identifyWalletType = (word: string): string | null => {
    // Ethereum, Polygon, and ERC20/ERC721 tokens (including USDT and USDC on these networks)
    if (/^0x[a-fA-F0-9]{40}$/.test(word)) {
        return 'Ethereum/Polygon compatible (could be ETH, MATIC, USDT, USDC, or other ERC20/ERC721 tokens)';
    }

    // Bitcoin
    if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(word) || /^bc1[a-z0-9]{39,59}$/.test(word)) {
        return 'Bitcoin';
    }

    // Tron (34 characters, starts with T)
    if (/^T[a-zA-Z0-9]{33}$/.test(word)) {
        return 'Tron (could be TRX or TRC20 tokens like USDT)';
    }

    // Solana (32-44 characters, base58 encoded)
    if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(word)) {
        return 'Solana (could be SOL, USDC, or other SPL tokens)';
    }

    // Algorand (58 characters, starts with A)
    if (/^A[A-Z2-7]{57}$/.test(word)) {
        return 'Algorand (could be ALGO, USDC, or other ASA tokens)';
    }

    // Ripple (25-35 characters, starts with r)
    if (/^r[0-9a-zA-Z]{24,34}$/.test(word)) {
        return 'Ripple';
    }

    // Litecoin (26-35 characters, starts with L, M, or ltc1)
    if (/^[LM3][a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(word) || /^ltc1[a-z0-9]{39,59}$/.test(word)) {
        return 'Litecoin';
    }

    // Dogecoin (34 characters, starts with D)
    if (/^D[5-9A-HJ-NP-U][1-9A-HJ-NP-Za-km-z]{32}$/.test(word)) {
        return 'Dogecoin';
    }

    // Cardano (58-103 characters, starts with addr1)
    if (/^addr1[a-z0-9]{58,103}$/.test(word)) {
        return 'Cardano';
    }

    // Polkadot (47-48 characters, starts with 1)
    if (/^1[a-zA-Z0-9]{46,47}$/.test(word)) {
        return 'Polkadot';
    }

    // Stellar (56 characters, starts with G)
    if (/^G[A-Z2-7]{55}$/.test(word)) {
        return 'Stellar';
    }

    // Monero (95 characters, starts with 4)
    if (/^4[0-9AB][1-9A-HJ-NP-Za-km-z]{93}$/.test(word)) {
        return 'Monero';
    }

    // Tezos (36 characters, starts with tz1, tz2, or tz3)
    if (/^tz[1-3][a-zA-Z0-9]{33}$/.test(word)) {
        return 'Tezos';
    }

    // If no match is found
    return null;
};

// Function to extract words while ignoring OnlyFans URLs
export const extractWordsFromTextWithoutOnlyFansURL = (text: string): string[] => {
    const words: string[] = [];
    const regex = /\S+/g;
    let match: RegExpExecArray | null;

    do {
        match = regex.exec(text);
        if (match !== null) {
            const word = match[0];
            if (!word.toLowerCase().startsWith('https://onlyfans.com/')) {
                words.push(word);
            }
        }
    } while (match !== null);

    return words;
};

/**
 * Extracts OnlyFans usernames from URLs
 * @param text Text to extract OnlyFans usernames from
 * @returns Array of usernames found in OnlyFans URLs
 */
export const extractOnlyFansUsernames = (text: string): string[] => {
    const usernames: string[] = [];

    // Pattern 1: onlyfans.com/1949237308/username (with or without protocol) - Check this FIRST
    const onlyFansNumericRegex = /(?:https?:\/\/)?(?:www\.)?onlyfans\.com\/\d+\/([a-zA-Z0-9._-]+)/gi;
    let match: RegExpExecArray | null;

    while ((match = onlyFansNumericRegex.exec(text)) !== null) {
        const username = match[1];

        if (username && !usernames.includes(username.toLowerCase())) {
            usernames.push(username.toLowerCase());
        }
    }

    // Pattern 2: onlyfans.com/username (with or without protocol) - Check this SECOND
    // Pattern 3: onlyfans.com/username/c2
    // Pattern 4: onlyfans.com/username/trial/ylkvmsahry6llvhzpeigwwudhacczqni
    // This regex excludes URLs that start with numbers followed by a slash to avoid conflicts
    const onlyFansRegex = /(?:https?:\/\/)?(?:www\.)?onlyfans\.com\/(?!\d+\/)([a-zA-Z0-9._-]+)(?:\/(?:c\d+|trial\/[a-z0-9]+))?/gi;

    while ((match = onlyFansRegex.exec(text)) !== null) {
        const username = match[1];

        if (username && !usernames.includes(username.toLowerCase())) {
            usernames.push(username.toLowerCase());
        }
    }

    // Pattern 5: @username (standalone @ mentions)
    const atMentionRegex = /@([a-zA-Z0-9._-]+)/g;

    while ((match = atMentionRegex.exec(text)) !== null) {
        const username = match[1];

        if (username && !usernames.includes(username.toLowerCase())) {
            usernames.push(username.toLowerCase());
        }
    }

    return usernames;
};

/**
 * Checks if restricted usernames are mentioned in the text based on configuration
 * @param text Text to check for username mentions
 * @param config OnlyFansSextforceBigBrother configuration object
 * @returns Array of restricted usernames found, or null if none found or blocking is disabled
 */
export const isRestrictedUsernameMentioned = (text: string, config: OnlyFansSextforceBigBrother | undefined): string[] | null => {
    // If blockAllUsernames is false or undefined, return null
    if (!config?.blockAllUsernames) {
        return null;
    }

    // Extract all usernames from the text
    const foundUsernames = extractOnlyFansUsernames(text);

    // If no usernames found, return null
    if (foundUsernames.length === 0) {
        return null;
    }

    // Filter out usernames that are in the allowedUsernames array
    const allowedUsernames = config.allowedUsernames || [];
    const restrictedUsernames = foundUsernames.filter(
        username => !allowedUsernames.some(allowed => allowed.toLowerCase() === username.toLowerCase()),
    );

    // Return null if no restricted usernames found, otherwise return the array
    return restrictedUsernames.length > 0 ? restrictedUsernames : null;
};

/**
 * Checks if the given message contains any restricted words
 * @param subscriber Subscriber object
 * @param message Message to check for restricted words
 * @param restrictedWords Set of restricted words
 * @returns Array of restricted words found in the message
 */
export const getRestrictedWordsInMessage = (subscriber: OnlyFansSubscriber, message: string, restrictedWords: Set<string>): string[] => {
    // Remove emojis from each word
    const noEmojis = emojiStrip(message);

    // Extract words, ignoring OnlyFans URLs
    const wordsInMessage = extractWordsFromTextWithoutOnlyFansURL(noEmojis.toLowerCase());

    // Filter out empty strings after emoji removal
    const cleanWords = wordsInMessage.filter(word => word.length > 0);

    let restrictedWordsFound = cleanWords.filter(word => restrictedWords.has(word));

    if (hasCommonStrings(subscriber.sextforce?.bigBrother?.restrictedWordsWhiteList, restrictedWordsFound)) {
        // Remove any words that are in the whitelist
        restrictedWordsFound = restrictedWordsFound.filter(
            word => !subscriber.sextforce?.bigBrother?.restrictedWordsWhiteList?.includes(word),
        );
    }

    // Test all words for mentions of a crypto wallet
    const walletWords = wordsInMessage.filter(word => identifyWalletType(word) !== null);

    if (walletWords.length > 0) {
        restrictedWordsFound.push(...walletWords);
    }

    // Test any custom restricted words
    if (subscriber.sextforce?.bigBrother?.restrictedWords) {
        const customRestrictedWordsFound = cleanWords.filter(word => subscriber.sextforce?.bigBrother?.restrictedWords?.includes(word));

        restrictedWordsFound.push(...customRestrictedWordsFound);
    }

    // Check for restricted usernames
    const restrictedUsernames = isRestrictedUsernameMentioned(message, subscriber.sextforce?.bigBrother);

    if (restrictedUsernames && restrictedUsernames.length > 0) {
        restrictedWordsFound.push(...restrictedUsernames);
    }

    // Remove duplicates
    restrictedWordsFound = Array.from(new Set(restrictedWordsFound));

    return restrictedWordsFound;
};

/**
 * Checks if the given editor state contains any restricted words
 * @param subscriber Subscriber object
 * @param editorState Editor state
 * @param restrictedWords Set of restricted words
 * @returns Array of restricted words found in the editor state
 */
export const getRestrictedWordsInEditorState = (subscriber: any, editorState: EditorState, restrictedWords: Set<string>): string[] => {
    const rawText = editorState.getCurrentContent().getPlainText();

    return getRestrictedWordsInMessage(subscriber, rawText, restrictedWords);
};
